import React, { useState } from 'react'

import {
    Popover,
    Select,
    MenuItem,
    Button,
    Typography,
    Box,
    IconButton,
} from '@material-ui/core'
import { Close } from '@material-ui/icons'

import { ListVendor } from '../../../../models'
import { BaseWorkorder } from '../../../../models'

interface VendorChangePopoverProps {
    open: boolean
    anchorEl: HTMLElement | null
    workorder: BaseWorkorder
    vendorList: ListVendor[]
    onClose: () => void
    onVendorChange: (workorder: BaseWorkorder, newVendorId: number) => void
}

export const VendorChangePopover: React.FC<VendorChangePopoverProps> = ({
    open,
    anchorEl,
    workorder,
    vendorList,
    onClose,
    onVendorChange,
}) => {
    // Don't render if no workorder
    if (!workorder) return null

    const [selectedVendor, setSelectedVendor] = useState(
        workorder.vendor_id?.toString() || '',
    )
    const [isSubmitting, setIsSubmitting] = useState(false)

    const handleSubmit = async () => {
        if (
            !selectedVendor ||
            selectedVendor === workorder.vendor_id?.toString()
        ) {
            onClose()
            return
        }

        setIsSubmitting(true)
        try {
            await onVendorChange(workorder, Number(selectedVendor))
            onClose()
        } catch (error) {
            console.error('Failed to update vendor:', error)
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleClose = () => {
        setSelectedVendor(workorder.vendor_id?.toString() || '')
        onClose()
    }

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
            }}
            PaperProps={{
                style: {
                    padding: '16px',
                    minWidth: '300px',
                    maxWidth: '400px',
                },
            }}
        >
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                style={{ marginBottom: '16px' }}
            >
                <Typography variant="h6">Change Vendor</Typography>
                <IconButton size="small" onClick={handleClose}>
                    <Close />
                </IconButton>
            </Box>

            <Box style={{ marginBottom: '24px' }}>
                <Typography variant="body2" style={{ marginBottom: '8px' }}>
                    Select New Vendor:
                </Typography>

                {(() => {
                    const availableVendors = vendorList.filter((vendor) =>
                        vendor.services.some(
                            (service) =>
                                service.name === workorder.service_name,
                        ),
                    )

                    if (availableVendors.length === 0) {
                        return (
                            <div
                                style={{
                                    padding: '12px',
                                    backgroundColor: '#fff3cd',
                                    border: '1px solid #ffeaa7',
                                    borderRadius: '4px',
                                    color: '#856404',
                                }}
                            >
                                No vendors available for{' '}
                                {workorder.service_name} service.
                            </div>
                        )
                    }

                    return (
                        <>
                            <Typography
                                variant="caption"
                                color="textSecondary"
                                style={{
                                    marginBottom: '8px',
                                    display: 'block',
                                }}
                            >
                                {availableVendors.length} vendor
                                {availableVendors.length !== 1 ? 's' : ''}{' '}
                                available for {workorder.service_name}
                            </Typography>
                            <Select
                                fullWidth
                                value={selectedVendor}
                                onChange={(e) =>
                                    setSelectedVendor(e.target.value as string)
                                }
                                variant="outlined"
                            >
                                <MenuItem value="">
                                    <em>No vendor assigned</em>
                                </MenuItem>
                                {availableVendors.map((vendor) => (
                                    <MenuItem key={vendor.id} value={vendor.id}>
                                        {vendor.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </>
                    )
                })()}
            </Box>

            <Box
                display="flex"
                style={{ gap: '8px', justifyContent: 'flex-end' }}
            >
                <Button
                    variant="outlined"
                    onClick={handleClose}
                    disabled={isSubmitting}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={
                        isSubmitting ||
                        selectedVendor === workorder.vendor_id?.toString() ||
                        vendorList.filter((vendor) =>
                            vendor.services.some(
                                (service) =>
                                    service.name === workorder.service_name,
                            ),
                        ).length === 0
                    }
                >
                    {isSubmitting
                        ? 'Updating...'
                        : vendorList.filter((vendor) =>
                              vendor.services.some(
                                  (service) =>
                                      service.name === workorder.service_name,
                              ),
                          ).length === 0
                        ? 'No Vendors Available'
                        : 'Update Vendor'}
                </Button>
            </Box>
        </Popover>
    )
}
